import React, { FC, useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import useConfirmDialog from '@/components/ConfirmDialogProvider';
import CustomIcon from '@/components/CustomIcon';
import Image from '@/components/Image';
import { Settings } from '@/Settings';
import { isDamsMediaItem } from '@/utils/typeChecks';
import { useControlledFieldArray } from '@/hooks/useControlledFieldArray';
import { Page } from '@/declarations/models/Page';
import { deepCopy } from '@/utils/object';
import { BaseBlock } from '@/declarations/models/blocks/BaseBlock';
import { SectionType } from '@/declarations/models/SectionType';
import { Draggable } from '@/declarations/Draggable';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import { MainCategory } from '@/declarations/Category';
import { rememberLastPage } from '@/views/ContentView/lastPage';
import Container from '../../components/Container';
import Styles from '../../assets/js/Styles';
import { useEditorConfiguration } from '../lib/configuration/EditorConfigurationContext';
import { Section as SectionModel } from '../../declarations/models/Section';
import SectionsController from '../../components/Section/SectionsController';
import Section from '../../components/Section/Section';
import SectionSettings from './SectionSettings';
import PageEditorSectionContent from './PageEditorSectionContent';
import AddPageSectionButtonGroup from './AddPageSectionButtonGroup';
import { CurrentSectionProvider, SectionsPath } from './CurrentSectionProvider';
import PageMetadata from './PageMetadata';
import { useEditorData, useEditorModel } from '../lib/components/EditorDataProvider';
import { usePageEditorFormState } from './PageEditorFormStateProvider';
import { EditorEventType, useEditorEventHandler, useEditorEventManager } from '../lib/eventManager/EditorEventManager';
import EventData, { EVENT_DATA_SECTION_ID } from './EventData';
import { CardSectionId, ListCardsForPage } from './ListCardsForPage';
import MissingMainHeadingWarning from './MissingMainHeadingWarning';

export const PageEditorContent: FC<ChildrenProp> = () => {
  const { t } = useTranslation('common');
  const { t: tComp } = useTranslation('components');
  const editorConfiguration = useEditorConfiguration();
  const eventManager = useEditorEventManager();
  const { siteId, modelId, skins, pageLocale, categories } = useEditorData();
  const [isReordering, setIsReordering] = useState<boolean>(false);
  const damsEmsDomain = Settings.viteEnvironmentIsProduction ? 'ems.dimu.org' : 'ems-test.dimu.org';
  useEditorEventHandler(async (event) => {
    if (event.type === EditorEventType.DRAGGING) {
      setIsReordering(true);
    }
  });

  const {
    fields: sections,
    append,
    move,
    remove,
    insert,
    update,
  } = useControlledFieldArray<Page, SectionsPath>({ name: 'content.sections' });

  const appendAndOpenSection = (item: SectionModel) => {
    append(item);
    // Wrap in timeout to allow the new component to render before firing the event. It's the new component that's listening for the event.
    setTimeout(() => {
      eventManager.fireEvent(EditorEventType.BLOCK_OR_SECTION_ADDED, {
        anchorId: `content.sections.${sections.length}`,
      });
    });
  };

  const defaultExpanded = useMemo<Array<string>>(() => {
    return sections.filter((s) => !s._collapse).map((s) => s.id);
  }, [sections]);

  const handleDuplicateSection = (section: SectionModel & { id?: string }, index: number) => {
    const sectionCopy = deepCopy(section);
    if (sectionCopy) {
      delete sectionCopy.id;
      sectionCopy.blocks?.forEach((block: BaseBlock & { id?: string }) => {
        // eslint-disable-next-line no-param-reassign
        delete block.id;
      });
      sectionCopy._recentlyPasted = true;
      insert(index + 1, sectionCopy);
    }
  };

  // The blocks after the split point move to a new section right below, with the same settings
  const handleSplitSection = (index: number, afterBlockIndex: number) => {
    const { id: _key, ...section } = sections[index];
    const blocks = section.blocks ?? [];
    if (afterBlockIndex < 0 || afterBlockIndex >= blocks.length - 1) return;
    const newSection = deepCopy(section);
    if (!newSection) return;
    delete newSection.introSection;
    newSection.title = '';
    newSection.blocks = newSection.blocks?.slice(afterBlockIndex + 1);
    newSection._recentlyPasted = true;
    update(index, { ...section, blocks: blocks.slice(0, afterBlockIndex + 1) });
    insert(index + 1, newSection);
  };

  const confirm = useConfirmDialog();

  // The next section shown in the editor, if it can be merged into this one: same type, both intro or neither.
  // The card section (article__links) isn't shown, so it's skipped.
  const getMergeableNextIndex = (index: number): number | null => {
    const section = sections[index];
    const nextIndex = sections.findIndex((s, j) => j > index && s.type !== SectionType.ARTICLE_LINKS);
    const next = sections[nextIndex];
    if (!next || section.type === SectionType.ARTICLE_HEAD) return null;
    if (next.type !== section.type || !!next.introSection !== !!section.introSection) return null;
    return nextIndex;
  };

  // The next section's blocks are added after this one's. This section keeps its settings; the next is removed.
  const handleMergeWithNext = async (index: number, nextIndex: number) => {
    const { id: _key, ...section } = sections[index];
    const next = sections[nextIndex];
    if (!(await confirm(tComp('PageEditor.mergeWithNextSectionConfirm')))) return;
    update(index, { ...section, blocks: [...(section.blocks ?? []), ...(deepCopy(next.blocks) ?? [])] });
    remove(nextIndex);
  };

  const getSectionTitle = (section: SectionModel): string => {
    if (section.type === SectionType.ARTICLE_BODY && section.introSection) {
      return t(`SectionType.article__body_introSection`);
    }
    return t(`SectionType.${section.type}`);
  };

  const model = useEditorModel<Page>();
  const { setCurrentFormData } = usePageEditorFormState();

  let imgMimeType = '';
  if (model?.image?.mimetype?.includes('png')) {
    imgMimeType = `mediaType=${model?.image?.mimetype}`;
  }

  useEffect(() => {
    if (model) {
      setCurrentFormData(model);
    }
  }, [model, setCurrentFormData]);

  // The page tree opens the way down to the page last edited
  useEffect(() => {
    if (siteId && modelId) rememberLastPage(siteId, modelId);
  }, [siteId, modelId]);

  const isMissingEventDate =
    model?.main_category === MainCategory.EVENT &&
    (model?.event_data?.time_periods ?? []).map((tp) => tp.start_date).filter(Boolean).length === 0;

  let imageSrc = model.image?.url ? `${model.image.url}?dimension=250x250&${imgMimeType}` : '';
  if (isDamsMediaItem(model.image) && model.image.m24?.ems_id) {
    imageSrc = `https://${damsEmsDomain}/image/${model.image.m24.ems_id}?dimension=250x250&${imgMimeType}`;
  }

  return (
    <Container
      sx={{ position: 'relative', backgroundColor: Styles.Colors.THEME_BG_COLOR_SECONDARY }}
      column
      gap={0}
      spaceBetween
      fullHeight
      fullWidth>
      <Container sx={{ overflow: 'auto' }} column top left fullHeight fullWidth>
        <SectionsController defaultExpanded={defaultExpanded} pr={0} pl={0} left fullHeight eventManager={eventManager}>
          <Section
            sectionId='page-editor-metadata'
            title={t('metadata')}
            subTitle={model.description || ''}
            color={Styles.Colors.LIGHT_GREY}
            disableDiffuse
            headerActions={
              <>
                <Image
                  key={model.image?.url}
                  sx={{
                    overflow: 'hidden',
                    objectFit: 'cover',
                    flex: 1,
                    width: '40px',
                    height: '40px',
                    borderRadius: Styles.Dimensions.RADIUS_ROUND_FULL,
                    border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                    mr: 2,
                    backgroundColor: Styles.Colors.MEDIUM_GREY,
                  }}
                  src={imageSrc}
                />
                <MaterialSymbol name='info' />
              </>
            }
            useEventManager={eventManager}>
            <PageMetadata isReordering={isReordering} />
          </Section>
          {model.main_category === MainCategory.EVENT && (
            <Section
              sectionId={EVENT_DATA_SECTION_ID}
              title={t('eventData')}
              headerActions={<CustomIcon name='calendar_event' />}
              color={Styles.Colors.STRONG_GREEN_TRANSPARENT}
              subTitle={
                isMissingEventDate && (
                  <Alert
                    sx={{
                      background: 'none',
                      fontStyle: 'italic',
                    }}
                    severity='warning'>
                    <span
                      style={{
                        padding: '0 0.25rem',
                      }}>
                      {tComp('PageEditor.missingEventDateWarning')}
                    </span>
                  </Alert>
                )
              }>
              <EventData />
            </Section>
          )}
          <MissingMainHeadingWarning />
          {sections.map(
            (section, i) =>
              section.type !== 'article__links' && (
                <CurrentSectionProvider key={section.id} section={section} sectionIndex={i}>
                  <Section
                    isHidden={section.visible === 'hidden'}
                    sectionId={`${'content.sections' as SectionsPath}.${i}`}
                    title={getSectionTitle(section)}
                    subTitle={section.title || ''}
                    childrenCount={section.blocks?.length || 0}
                    headerActions={
                      <>
                        <SectionSettings
                          onRemoveSection={() => remove(i)}
                          onDuplicateSection={() => handleDuplicateSection(section, i)}
                          onMergeWithNext={(() => {
                            const nextIndex = getMergeableNextIndex(i);
                            return nextIndex === null ? undefined : () => handleMergeWithNext(i, nextIndex);
                          })()}
                        />
                        {!editorConfiguration.isSectionAvailable(section.type) && (
                          <MaterialSymbol name='error' color='error' />
                        )}
                      </>
                    }
                    type={Draggable.SECTION}
                    index={i}
                    onReorder={move}
                    value={section}
                    useEventManager={eventManager}
                    isPasted={section._recentlyPasted}
                    draggable>
                    <PageEditorSectionContent
                      useEventManager={eventManager}
                      onSplitSection={
                        section.type !== SectionType.ARTICLE_HEAD
                          ? (afterBlockIndex) => handleSplitSection(i, afterBlockIndex)
                          : undefined
                      }
                    />
                  </Section>
                </CurrentSectionProvider>
              ),
          )}
          <Section
            sectionId={CardSectionId}
            title={t('SectionType.article__links')}
            color={Styles.Colors.LIGHT_GREY}
            headerActions={<MaterialSymbol name='info' />}
            useEventManager={eventManager}>
            <ListCardsForPage
              siteId={siteId}
              pageId={modelId}
              skins={skins}
              locale={pageLocale}
              categories={categories}
            />
          </Section>
        </SectionsController>
      </Container>
      <Container
        px={3}
        pt={1}
        pb={1}
        spaceBetween
        fullWidth
        sx={{ backgroundColor: 'white', borderTop: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}` }}>
        <AddPageSectionButtonGroup onAddSection={appendAndOpenSection} />
      </Container>
    </Container>
  );
};

export default PageEditorContent;
